import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PWAInstallBanner from "./components/PWAInstallBanner";
import "./styles/pwa-layout.css";
import "./styles/akagari.css";
import "./styles/office-suite.css";
import "./styles/excel.css";
import "./responsive.css";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import { NotificationProvider } from './context/NotificationContext';
import Notifications from './page/Notifications';
import axios from "axios";
import axiosInstance from "./utils/axiosInstance";
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useInView } from "framer-motion";
import * as Icon from "react-bootstrap-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// Placeholder imports (unchanged)
import Dashboard from "./page/Mudugudu";
import DashboardBohejuru from "./components/Abayobozi bo hejuru";
import DashboardIsibo from "./components/Isibodashboard";
import DashboardUmunyamabanga from "./components/DashboardUmunyamabanga";
import SuperAdminDashboard from "./components/SuperAdminDashboard.jsx";
import ImibareNavigation from "./components/ImibareNavigation";
import VillageInfoSystem from "./components/VillageInfoSystem";
import InjiraBackground from "./components/InjiraBackground";
import IsiboLoginPage from "./components/IsiboLoginPage";
import HeroCarousel from "./components/HeroCarousel";
import IgitaboLogo from "./components/IgitaboLogo";
import ViewReport from "./components/ViewReport";
import ReceiverSignaturePage from "./components/ReceiverSignaturePage";
import UmutekanoLogin from "./components/UmutekanoLogin";
import UmutekanoDashboard from "./components/UmutekanoDashboard";
import Spreadsheet from "./components/Spreadsheet";
import WordTableEditor from "./components/WordTableEditor";
import UmusanzuEjoheza from "./page/UmusanzuEjoheza";
import IsukuIsukura from "./page/IsukuIsukura";

// ===================== NIDA VALIDATION HELPERS =====================
const NidaValidator = {
  clean: (value) => value.replace(/\s/g, "").replace(/\D/g, ""),
  format: (value) => {
    const cleaned = NidaValidator.clean(value).slice(0, 16);
    let formatted = "";
    for (let i = 0; i < cleaned.length; i++) {
      if (i === 1 || i === 5 || i === 6 || i === 13 || i === 14) formatted += " ";
      formatted += cleaned[i];
    }
    return formatted;
  },
  validate: (value) => {
    if (!value || value.trim() === "") return null;
    const cleaned = NidaValidator.clean(value);
    if (!/^\d+$/.test(cleaned)) return { valid: false, error: "Indangamuntu igomba kuba imibare gusa" };
    if (cleaned.length > 0 && cleaned.length < 3) return { valid: false, error: null };
    if (!cleaned.startsWith("119") && !cleaned.startsWith("120"))
      return { valid: false, error: "Indangamuntu igomba gutangira na 119 cyangwa 120" };
    if (cleaned.length !== 16)
      return { valid: false, error: `Indangamuntu igomba kuba imibare 16 (ubu ufite: ${cleaned.length})` };
    return { valid: true, error: null };
  },
  getPrefix: (value) => {
    if (!value) return null;
    const cleaned = NidaValidator.clean(value);
    if (cleaned.length < 3) return null;
    if (cleaned.startsWith("119")) return { prefix: "119", color: "#3b82f6", label: "NIDA 119" };
    if (cleaned.startsWith("120")) return { prefix: "120", color: "#06b6d4", label: "NIDA 120" };
    return { prefix: cleaned.substring(0, 3), color: "#ef4444", label: "Ntiyemewe" };
  },
  getCountDisplay: (value) => {
    const cleaned = NidaValidator.clean(value);
    if (cleaned.length === 0) return null;
    return {
      current: cleaned.length,
      total: 16,
      isComplete: cleaned.length === 16,
      percentage: Math.min((cleaned.length / 16) * 100, 100),
    };
  },
};

// ===================== THEME CONTEXT =====================
// Dark/Light Mode Theme Hook
export const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('app-theme');
    return saved || 'dark';
  });

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('app-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  return { theme, toggleTheme };
};

// Theme Toggle Button Component
export const ThemeToggle = ({ className = '' }) => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <button 
      className={`theme-toggle-btn ${className}`}
      onClick={toggleTheme}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      <i className={`bi ${theme === 'dark' ? 'bi-sun-fill' : 'bi-moon-fill'}`}></i>
      <span className="d-none d-sm-inline">{theme === 'dark' ? 'Light' : 'Dark'}</span>
    </button>
  );
};

// ===================== GLOBAL STYLES =====================
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');

    /* Dark Theme (Default) */
    :root, [data-theme="dark"] {
      --primary: #003580;
      --primary-dark: #002a6b;
      --primary-light: #1a56b0;
      --accent: #c8a84b;
      --accent-light: #d4b96a;
      --danger: #b91c1c;
      --success: #1a7a4a;
      --warning: #b45309;
      --bg-body: #0a1628;
      --bg-card: rgba(0, 30, 70, 0.65);
      --glass-bg: rgba(0, 30, 70, 0.82);
      --glass-border: 1px solid rgba(200, 168, 75, 0.18);
      --input-bg: rgba(0, 20, 55, 0.85);
      --text-main: #f1f5f9;
      --text-muted: #94a3b8;
      --text-dim: #64748b;
      --border-color: #0f2a50;
      --border-light: #1a3a6b;
      --card-bg: #001845;
      --hover-bg: rgba(200, 168, 75, 0.08);
      --shadow-color: rgba(0, 0, 0, 0.3);
      --radius-sm: 16px;
      --radius-md: 20px;
      --radius-lg: 28px;
      --radius-xl: 32px;
      --shadow-glow: 0 0 40px rgba(0, 53, 128, 0.25);
      --shadow-card: 0 8px 32px rgba(0, 0, 0, 0.2);
      --transition-smooth: cubic-bezier(0.4, 0, 0.2, 1);
      --transition-bounce: cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    /* Light Theme */
    [data-theme="light"] {
      --primary: #003580;
      --primary-dark: #002a6b;
      --primary-light: #1a56b0;
      --accent: #c8a84b;
      --accent-light: #d4b96a;
      --danger: #b91c1c;
      --success: #1a7a4a;
      --warning: #b45309;
      --bg-body: #f8fafc;
      --bg-card: rgba(255, 255, 255, 0.9);
      --glass-bg: rgba(255, 255, 255, 0.85);
      --glass-border: 1px solid rgba(99, 102, 241, 0.12);
      --input-bg: rgba(241, 245, 249, 0.95);
      --text-main: #0f172a;
      --text-muted: #64748b;
      --text-dim: #94a3b8;
      --border-color: #e2e8f0;
      --border-light: #cbd5e1;
      --card-bg: #ffffff;
      --hover-bg: rgba(99, 102, 241, 0.05);
      --shadow-color: rgba(0, 0, 0, 0.1);
      --radius-sm: 16px;
      --radius-md: 20px;
      --radius-lg: 28px;
      --radius-xl: 32px;
      --shadow-glow: 0 0 40px rgba(99, 102, 241, 0.1);
      --shadow-card: 0 8px 32px rgba(0, 0, 0, 0.1);
      --transition-smooth: cubic-bezier(0.4, 0, 0.2, 1);
      --transition-bounce: cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    /* Theme Transition — only color properties, not layout */
    body {
      transition: background-color 0.3s ease, color 0.3s ease;
    }

    /* Theme Toggle Button */
    .theme-toggle-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: var(--bg-card);
      border: 1px solid var(--border-color);
      border-radius: 12px;
      color: var(--text-main);
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      min-height: 44px;
    }

    .theme-toggle-btn:hover {
      background: var(--hover-bg);
      border-color: var(--primary);
      transform: translateY(-1px);
    }

    .theme-toggle-btn:active {
      transform: scale(0.98);
    }

    .theme-toggle-btn i {
      font-size: 18px;
      transition: transform 0.3s ease;
    }

    .theme-toggle-btn:active i {
      transform: rotate(360deg);
    }

    @media (max-width: 575.98px) {
      .theme-toggle-btn {
        padding: 8px 12px;
        min-width: 44px;
        justify-content: center;
      }
      .theme-toggle-btn span {
        display: none;
      }
    }

    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
    }

    html {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
      scroll-behavior: smooth;
      overflow-x: hidden;
      /* Allow native scroll on mobile */
      -webkit-overflow-scrolling: touch;
    }

    body {
      font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: var(--bg-body);
      color: var(--text-main);
      min-height: 100vh;
      min-height: 100dvh;
      margin: 0;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      overflow-x: hidden;
      overflow-y: auto;
      /* Critical: allow vertical scroll everywhere */
      touch-action: pan-y;
      overscroll-behavior-y: auto;
    }

    #root {
      min-height: 100vh;
      min-height: 100dvh;
      width: 100%;
      max-width: 100%;
      overflow-x: clip;
      overflow-y: visible;
      display: flex;
      flex-direction: column;
    }

    picture {
      display: block;
      max-width: 100%;
    }

    img, svg, video, canvas {
      display: block;
      max-width: 100%;
    }

    img, video {
      height: auto;
    }

    button, input, select, textarea {
      font: inherit;
      max-width: 100%;
    }

    textarea {
      resize: vertical;
      min-height: 120px;
    }

    button, [role="button"] {
      touch-action: manipulation;
    }

    input[type="file"] {
      width: 100%;
      max-width: 100%;
      cursor: pointer;
    }

    input[type="file"]::file-selector-button {
      margin-inline-end: 0.75rem;
      min-height: 40px;
      border: 0;
      border-radius: 10px;
      padding: 0.65rem 0.95rem;
      font: inherit;
      font-weight: 700;
      background: linear-gradient(135deg, rgba(99,102,241,0.2), rgba(6,182,212,0.16));
      color: var(--text-main);
      cursor: pointer;
    }

    :where(.table-scroll-wrapper, .ab-table-scroll, .el-table-scroll, .ub-table-scroll) {
      overflow-x: auto;
      overscroll-behavior-x: contain;
      -webkit-overflow-scrolling: touch;
    }

    @media (max-width: 767px) {
      button,
      [type="button"],
      [type="submit"],
      [type="reset"] {
        min-height: 44px;
      }

      input:not([type="checkbox"]):not([type="radio"]),
      select,
      textarea {
        width: 100%;
        font-size: 16px;
      }

      input[type="file"] {
        min-height: 48px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      html {
        scroll-behavior: auto;
      }
    }

    /* ==================== PAGE CENTER ==================== */
    .page-center {
      min-height: 100vh;
      min-height: 100dvh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem 1rem;
    }

    @media (max-width: 480px) {
      .page-center {
        padding: 1rem 0.75rem;
        align-items: flex-start;
        padding-top: 2rem;
      }
    }

    @media (max-width: 320px) {
      .page-center {
        padding: 0.75rem 0.5rem;
        padding-top: 1.5rem;
      }
    }

    /* ==================== FORM CONTAINER ==================== */
    .form-container {
      background: var(--glass-bg);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      border: var(--glass-border);
      border-radius: var(--radius-lg);
      padding: 3rem 2.5rem;
      max-width: 680px;
      width: 100%;
      box-shadow: 0 0 25px rgba(99, 102, 241, 0.35);
      position: relative;
    }

    @media (max-width: 480px) {
      .form-container {
        padding: 1.75rem 1.25rem;
        border-radius: 20px;
        box-shadow: 0 0 15px rgba(99, 102, 241, 0.2);
      }
    }

    @media (max-width: 320px) {
      .form-container {
        padding: 1.5rem 1rem;
        border-radius: 16px;
      }
    }

    @media (min-width: 481px) and (max-width: 768px) {
      .form-container {
        padding: 2.5rem 2rem;
        max-width: 580px;
      }
    }

    /* ==================== FORM TITLE ==================== */
    .form-title {
      font-size: 2.8rem;
      font-weight: 700;
      background: linear-gradient(90deg, var(--primary), var(--accent));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-align: center;
      margin-bottom: 2rem;
    }

    @media (max-width: 480px) {
      .form-title { font-size: 1.7rem; margin-bottom: 1.5rem; }
    }

    @media (max-width: 320px) {
      .form-title { font-size: 1.4rem; margin-bottom: 1.25rem; }
    }

    @media (min-width: 481px) and (max-width: 768px) {
      .form-title { font-size: 2.2rem; }
    }

    /* ==================== MODERN INPUT ==================== */
    .modern-input {
      background: var(--input-bg);
      border: 1px solid rgba(99,102,241,0.2);
      border-radius: var(--radius-sm);
      color: white;
      padding: 1.25rem 1.25rem 1.25rem 4.2rem;
      font-size: 1.05rem;
      height: 58px;
      width: 100%;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      touch-action: manipulation;
      transition: all 0.3s var(--transition-smooth);
      outline: none;
    }

    @media (max-width: 480px) {
      .modern-input {
        font-size: 16px !important;
        height: 52px;
        padding: 1rem 1rem 1rem 3.2rem;
        border-radius: 14px;
      }
    }

    @media (max-width: 320px) {
      .modern-input {
        height: 48px;
        padding: 0.875rem 0.875rem 0.875rem 2.8rem;
        border-radius: 12px;
      }
    }

    .modern-input:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15), 0 0 20px rgba(99, 102, 241, 0.08);
      background: rgba(30, 41, 59, 0.95);
    }

    .modern-input.input-error {
      border-color: var(--danger) !important;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12) !important;
    }

    .modern-input.input-valid {
      border-color: var(--success) !important;
      box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1) !important;
    }

    .modern-input::placeholder {
      color: rgba(148, 163, 184, 0.5);
    }

    @supports (-webkit-touch-callout: none) {
      .modern-input { font-size: 16px !important; }
    }

    /* ==================== INPUT ICON ==================== */
    .input-icon {
      position: absolute;
      left: 1.5rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-muted);
      font-size: 1.4rem;
      transition: color 0.3s ease;
      pointer-events: none;
    }

    @media (max-width: 480px) {
      .input-icon { left: 1.1rem; font-size: 1.2rem; }
    }

    @media (max-width: 320px) {
      .input-icon { left: 0.9rem; font-size: 1.1rem; }
    }

    /* ==================== BUTTONS ==================== */
    .btn-gradient {
      background: linear-gradient(135deg, #003580, #1a56b0);
      border: none;
      color: white;
      font-weight: 700;
      width: 100%;
      height: 58px;
      border-radius: var(--radius-sm);
      margin-top: 1rem;
      touch-action: manipulation;
      transition: all 0.3s var(--transition-smooth);
      cursor: pointer;
      font-size: 1.05rem;
      letter-spacing: 0.02em;
      position: relative;
      overflow: hidden;
    }

    @media (max-width: 480px) {
      .btn-gradient { height: 52px; font-size: 1rem; border-radius: 14px; }
    }

    @media (max-width: 320px) {
      .btn-gradient { height: 48px; font-size: 0.95rem; border-radius: 12px; }
    }

    .btn-gradient:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(99, 102, 241, 0.35);
    }

    .btn-gradient:active:not(:disabled) { transform: translateY(0) scale(0.98); }
    .btn-gradient:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }

    .btn-back {
      background: transparent;
      border: 1px solid rgba(255,255,255,0.08);
      color: var(--text-muted);
      padding: 0.5rem 1.2rem;
      border-radius: 50px;
      font-size: 0.9rem;
      text-decoration: none;
      transition: all 0.3s;
      display: inline-flex;
      align-items: center;
      margin-bottom: 1.5rem;
      touch-action: manipulation;
      cursor: pointer;
      min-height: 44px;
    }

    .btn-back:hover { background: rgba(255,255,255,0.05); color: white; border-color: var(--primary); }
    .btn-back:active { transform: scale(0.96); }

    /* ==================== SPLASH ==================== */
    .splash-container {
      position: fixed;
      inset: 0;
      background: linear-gradient(135deg, #000d24, #001845, #002a6b);
      z-index: 9999;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: white;
      padding: 1rem;
    }

    .splash-title {
      font-size: 2rem;
      font-weight: 600;
      background: linear-gradient(90deg, #c8a84b, #f0d080);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-align: center;
    }

    @media (max-width: 480px) { .splash-title { font-size: 1.4rem; } }
    @media (max-width: 320px) { .splash-title { font-size: 1.2rem; } }

    .splash-subtitle {
      font-size: 1.1rem;
      color: #94a3b8;
      opacity: 0.9;
      text-align: center;
    }

    @media (max-width: 480px) { .splash-subtitle { font-size: 0.9rem; } }

    /* ==================== ANIMATIONS ==================== */
    @keyframes blink { 0%, 92%, 100% { transform: scaleY(1); } 94% { transform: scaleY(0.04); } }
    @keyframes shakeError { 0%, 100% { transform: translateX(0); } 10%, 50%, 90% { transform: translateX(-4px); } 30%, 70% { transform: translateX(4px); } }
    @keyframes fadeInError { from { opacity: 0; transform: translateY(-4px); max-height: 0; } to { opacity: 1; transform: translateY(0); max-height: 80px; } }
    @keyframes validPulse { 0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.25); } 70% { box-shadow: 0 0 0 6px rgba(16,185,129,0); } 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); } }
    @keyframes badgePopIn { from { opacity: 0; transform: scale(0.7); } to { opacity: 1; transform: scale(1); } }
    @keyframes shimmerGradient { 0%, 100% { background-position: 0% center; } 50% { background-position: 100% center; } }
    @keyframes cursorBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
    @keyframes rippleOut { 0% { transform: scale(0.8); opacity: 0.6; } 100% { transform: scale(2.5); opacity: 0; } }
    @keyframes morphBlob { 0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; } 25% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; } 50% { border-radius: 50% 50% 30% 70% / 40% 70% 50% 60%; } 75% { border-radius: 40% 60% 60% 40% / 60% 40% 60% 40%; } }
    @keyframes gridPulse { 0%, 100% { opacity: 0.02; } 50% { opacity: 0.06; } }
    @keyframes scanLine { 0% { top: -10%; } 100% { top: 110%; } }
    @keyframes aurora { 0%, 100% { background-position: 0% 50%; opacity: 0.3; } 50% { background-position: 100% 50%; opacity: 0.5; } }
    @keyframes scrollTrack { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
    @keyframes float3D { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
    @keyframes glowPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.8; } }
    @keyframes borderGlow { 0%, 100% { border-color: rgba(99,102,241,0.15); } 50% { border-color: rgba(99,102,241,0.35); } }

    .nida-error-msg { animation: fadeInError 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    .nida-badge { animation: badgePopIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
    .input-shake { animation: shakeError 0.4s ease; }

    .input-status-icon {
      position: absolute;
      right: 1.2rem;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1.1rem;
      z-index: 5;
      pointer-events: none;
      transition: all 0.3s ease;
    }

    @media (max-width: 480px) { .input-status-icon { right: 0.9rem; font-size: 1rem; } }

    .nida-progress-bar { height: 3px; background: rgba(99,102,241,0.08); border-radius: 10px; margin-top: 8px; overflow: hidden; }
    .nida-progress-fill { height: 100%; border-radius: 10px; transition: all 0.3s ease; }

    .nida-count {
      display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px;
      border-radius: 8px; font-size: 0.68rem; font-weight: 700;
      font-family: 'JetBrains Mono', 'Fira Code', monospace; letter-spacing: 0.05em;
    }

    /* ==================== HOME PAGE ==================== */
    .home-page {
      min-height: 100vh;
      min-height: 100dvh;
      overflow-x: hidden;
      overflow-y: visible;
      background: #f4f6f9;
      position: relative;
      display: flex;
      flex-direction: column;
      touch-action: pan-y;
    }

    .home-page::before { display: none; }

    /* ==================== NAVBAR ==================== */
    .home-navbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 1000;
      padding: 1rem 2rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: all 0.4s var(--transition-smooth);
    }

    .home-navbar.scrolled {
      background: rgba(0, 35, 80, 0.97);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 2px solid #c8a84b;
      box-shadow: 0 2px 16px rgba(0,0,0,0.18);
      padding: 0.6rem 2rem;
    }

    @media (max-width: 480px) {
      .home-navbar { padding: 0.75rem 1rem; }
      .home-navbar.scrolled { padding: 0.5rem 1rem; }
    }

    @media (max-width: 320px) {
      .home-navbar { padding: 0.6rem 0.75rem; }
    }

    .nav-logo {
      display: flex;
      align-items: center;
      gap: 10px;
      text-decoration: none;
      cursor: pointer;
      -webkit-user-select: none;
      user-select: none;
    }

    .nav-logo-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
    }

    .nav-logo-text {
      font-size: 1.3rem;
      font-weight: 700;
      background: linear-gradient(90deg, #c8a84b, #f0d080);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    @media (max-width: 480px) {
      .nav-logo-text { font-size: 1.05rem; }
      .nav-logo-icon { }
    }

    @media (max-width: 320px) {
      .nav-logo-text { font-size: 0.95rem; }
      .nav-logo-icon { }
    }

    .nav-btn {
      padding: 10px 20px;
      border-radius: 12px;
      background: rgba(200,168,75,0.1);
      border: 1px solid rgba(200,168,75,0.25);
      color: #c8a84b;
      font-weight: 600;
      font-size: 0.88rem;
      cursor: pointer;
      transition: all 0.3s ease;
      backdrop-filter: blur(10px);
      display: flex;
      align-items: center;
      gap: 6px;
      min-height: 44px;
      touch-action: manipulation;
    }

    .nav-btn:hover {
      background: rgba(200,168,75,0.18);
      border-color: rgba(200,168,75,0.45);
      color: white;
      transform: translateY(-1px);
    }

    .nav-btn:active { transform: scale(0.96); }

    @media (max-width: 480px) {
      .nav-btn { padding: 8px 14px; font-size: 0.82rem; border-radius: 10px; }
    }

    @media (max-width: 320px) {
      .nav-btn { padding: 6px 10px; font-size: 0.78rem; min-height: 40px; }
    }

    /* ==================== HERO SECTION ==================== */
    .hero-section {
      position: relative;
      z-index: 1;
      min-height: 100vh;
      min-height: 100dvh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 7rem 2rem 4rem;
      text-align: center;
    }

    @media (max-width: 768px) {
      .hero-section { padding: 6rem 1.25rem 3rem; }
    }

    @media (max-width: 480px) {
      .hero-section {
        padding: 5.5rem 1rem 2.5rem;
        min-height: auto;
        min-height: calc(100vh - 60px);
      }
    }

    @media (max-width: 320px) {
      .hero-section { padding: 5rem 0.75rem 2rem; }
    }

    @media (max-height: 600px) and (orientation: landscape) {
      .hero-section {
        min-height: auto;
        padding: 5rem 2rem 2rem;
      }
    }

    .hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 20px;
      border-radius: 50px;
      background: rgba(200,168,75,0.08);
      border: 1px solid rgba(200,168,75,0.25);
      color: #c8a84b;
      font-size: 0.85rem;
      font-weight: 600;
      margin-bottom: 2rem;
      backdrop-filter: blur(10px);
    }

    @media (max-width: 480px) {
      .hero-badge { font-size: 0.75rem; padding: 6px 14px; margin-bottom: 1.5rem; gap: 6px; }
    }

    @media (max-width: 320px) {
      .hero-badge { font-size: 0.7rem; padding: 5px 12px; }
    }

    .hero-badge-dot {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #c8a84b;
      box-shadow: 0 0 8px rgba(200,168,75,0.5);
    }

    @media (max-width: 480px) {
      .hero-badge-dot { width: 6px; height: 6px; }
    }

    .hero-title {
      font-size: clamp(2rem, 7vw, 4.5rem);
      font-weight: 800;
      line-height: 1.08;
      margin-bottom: 1.5rem;
      color: white;
      letter-spacing: -0.02em;
    }

    @media (max-width: 480px) {
      .hero-title { font-size: clamp(1.75rem, 8vw, 2.5rem); margin-bottom: 1rem; line-height: 1.15; }
    }

    @media (max-width: 320px) {
      .hero-title { font-size: clamp(1.5rem, 9vw, 2rem); }
    }

    .hero-title-gradient {
      background: linear-gradient(135deg, #c8a84b 0%, #f0d080 50%, #c8a84b 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-size: 200% auto;
      animation: shimmerGradient 4s ease-in-out infinite;
    }

    .hero-subtitle {
      font-size: clamp(0.9rem, 2.5vw, 1.3rem);
      color: #94a3b8;
      max-width: 600px;
      margin: 0 auto 2.5rem;
      line-height: 1.7;
      font-weight: 400;
    }

    @media (max-width: 480px) {
      .hero-subtitle {
        font-size: clamp(0.85rem, 3.5vw, 1rem);
        margin: 0 auto 2rem;
        line-height: 1.6;
        padding: 0 0.25rem;
      }
    }

    @media (max-width: 320px) {
      .hero-subtitle { font-size: 0.82rem; margin-bottom: 1.5rem; }
    }

    .hero-cta-group {
      display: flex;
      gap: 1rem;
      justify-content: center;
      flex-wrap: wrap;
    }

    @media (max-width: 480px) {
      .hero-cta-group {
        flex-direction: column;
        gap: 0.75rem;
        width: 100%;
        max-width: 340px;
        margin: 0 auto;
      }
    }

    .hero-cta-primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 16px 36px;
      border-radius: 16px;
      background: linear-gradient(135deg, #003580, #1a56b0);
      color: white;
      font-weight: 700;
      font-size: 1.05rem;
      border: none;
      cursor: pointer;
      transition: all 0.3s var(--transition-smooth);
      box-shadow: 0 8px 30px rgba(0,53,128,0.4);
      touch-action: manipulation;
      text-decoration: none;
      min-height: 52px;
      position: relative;
      overflow: hidden;
    }

    .hero-cta-primary:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 40px rgba(0,53,128,0.5);
      color: white;
    }

    .hero-cta-primary:active { transform: translateY(-1px) scale(0.98); }

    @media (max-width: 480px) {
      .hero-cta-primary {
        padding: 14px 28px;
        font-size: 0.95rem;
        width: 100%;
        border-radius: 14px;
        min-height: 50px;
      }
    }

    @media (max-width: 320px) {
      .hero-cta-primary { padding: 12px 24px; font-size: 0.9rem; min-height: 48px; }
    }

    .hero-cta-secondary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 16px 36px;
      border-radius: 16px;
      background: rgba(255,255,255,0.04);
      border: 1px solid rgba(255,255,255,0.1);
      color: #e2e8f0;
      font-weight: 600;
      font-size: 1.05rem;
      cursor: pointer;
      transition: all 0.3s var(--transition-smooth);
      backdrop-filter: blur(10px);
      touch-action: manipulation;
      text-decoration: none;
      min-height: 52px;
    }

    .hero-cta-secondary:hover {
      background: rgba(255,255,255,0.08);
      border-color: rgba(99,102,241,0.35);
      color: white;
      transform: translateY(-2px);
    }

    .hero-cta-secondary:active { transform: scale(0.98); }

    @media (max-width: 480px) {
      .hero-cta-secondary {
        padding: 14px 28px;
        font-size: 0.95rem;
        width: 100%;
        border-radius: 14px;
        min-height: 50px;
      }
    }

    @media (max-width: 320px) {
      .hero-cta-secondary { padding: 12px 24px; font-size: 0.9rem; min-height: 48px; }
    }

    /* ==================== SCROLL INDICATOR ==================== */
    .scroll-indicator {
      margin-top: 4rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    @media (max-width: 480px) {
      .scroll-indicator { margin-top: 2.5rem; }
    }

    @media (max-height: 600px) {
      .scroll-indicator { display: none; }
    }

    /* ==================== STATS SECTION ==================== */
    .stats-section {
      position: relative;
      z-index: 1;
      padding: 4rem 2rem;
      /* Ensure sections don't block scroll */
      touch-action: pan-y;
    }

    @media (max-width: 480px) { .stats-section { padding: 3rem 1rem; } }
    @media (max-width: 320px) { .stats-section { padding: 2.5rem 0.75rem; } }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1.25rem;
      max-width: 900px;
      margin: 0 auto;
    }

    @media (max-width: 768px) {
      .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
    }

    @media (max-width: 380px) {
      .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
    }

    .stat-card {
      background: #ffffff;
      border: 1px solid #dce3ed;
      border-top: 3px solid #003580;
      border-radius: 8px;
      padding: 1.75rem 1.25rem;
      text-align: center;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }

    .stat-card:hover {
      border-top-color: #c8a84b;
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0,53,128,0.1);
    }

    @media (max-width: 480px) {
      .stat-card { padding: 1.25rem 1rem; border-radius: 16px; }
    }

    @media (max-width: 320px) {
      .stat-card { padding: 1rem 0.75rem; border-radius: 14px; }
    }

    .stat-number {
      font-size: 2.2rem;
      font-weight: 800;
      color: #003580;
      line-height: 1;
      margin-bottom: 0.4rem;
    }

    @media (max-width: 480px) { .stat-number { font-size: 1.8rem; } }
    @media (max-width: 320px) { .stat-number { font-size: 1.5rem; } }

    .stat-label {
      font-size: 0.82rem;
      color: #4a5568;
      font-weight: 600;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    @media (max-width: 480px) { .stat-label { font-size: 0.75rem; } }
    @media (max-width: 320px) { .stat-label { font-size: 0.7rem; } }

    /* ==================== SECTION HEADERS ==================== */
    .section-header {
      text-align: center;
      margin-bottom: 3rem;
    }

    @media (max-width: 480px) { .section-header { margin-bottom: 2rem; } }

    .section-tag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 14px;
      border-radius: 4px;
      background: #003580;
      border: none;
      color: #ffffff;
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-bottom: 1rem;
    }

    @media (max-width: 480px) {
      .section-tag { font-size: 0.7rem; padding: 5px 12px; }
    }

    .section-title {
      font-size: clamp(1.6rem, 4vw, 2.4rem);
      font-weight: 700;
      color: #0d1b2e;
      margin-bottom: 0.75rem;
      line-height: 1.2;
    }

    @media (max-width: 480px) {
      .section-title { font-size: clamp(1.3rem, 5vw, 1.8rem); }
    }

    @media (max-width: 320px) {
      .section-title { font-size: clamp(1.15rem, 6vw, 1.5rem); }
    }

    .section-desc {
      font-size: 1rem;
      color: #4a5568;
      max-width: 520px;
      margin: 0 auto;
      line-height: 1.6;
    }

    @media (max-width: 480px) {
      .section-desc { font-size: 0.88rem; padding: 0 0.5rem; }
    }

    @media (max-width: 320px) {
      .section-desc { font-size: 0.82rem; }
    }

    /* ==================== PORTALS SECTION ==================== */
    .portals-section {
      position: relative;
      z-index: 1;
      padding: 5rem 2rem;
      touch-action: pan-y;
    }

    @media (max-width: 768px) { .portals-section { padding: 3.5rem 1.25rem; } }
    @media (max-width: 480px) { .portals-section { padding: 3rem 1rem; } }
    @media (max-width: 320px) { .portals-section { padding: 2.5rem 0.75rem; } }

    .portals-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
      max-width: 1100px;
      margin: 0 auto;
    }

    @media (max-width: 900px) {
      .portals-grid { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 600px) {
      .portals-grid { grid-template-columns: 1fr; gap: 1rem; }
    }

    .portal-card-new {
      background: #ffffff;
      border: 1px solid #dce3ed;
      border-left: 4px solid #003580;
      border-radius: 8px;
      padding: 2rem 1.75rem;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: all 0.3s ease;
      touch-action: manipulation;
      min-height: 220px;
      display: flex;
      flex-direction: column;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }

    .portal-card-new:hover {
      transform: translateY(-4px);
      border-left-color: #c8a84b;
      box-shadow: 0 8px 28px rgba(0,53,128,0.12);
    }

    .portal-card-new:active {
      transform: translateY(-2px) scale(0.99);
    }

    @media (max-width: 480px) {
      .portal-card-new {
        padding: 1.75rem 1.5rem;
        border-radius: 20px;
        min-height: 190px;
      }
    }

    @media (max-width: 320px) {
      .portal-card-new {
        padding: 1.5rem 1.25rem;
        border-radius: 18px;
        min-height: 170px;
      }
    }

    .portal-icon-wrapper {
      width: 60px; height: 60px;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.25rem;
      font-size: 1.5rem;
      transition: all 0.3s ease;
    }

    .portal-card-new:hover .portal-icon-wrapper {
      transform: scale(1.1) rotate(-3deg);
    }

    @media (max-width: 480px) {
      .portal-icon-wrapper { width: 52px; height: 52px; font-size: 1.3rem; border-radius: 14px; margin-bottom: 1rem; }
    }

    @media (max-width: 320px) {
      .portal-icon-wrapper { width: 46px; height: 46px; font-size: 1.2rem; border-radius: 12px; }
    }

    .portal-card-title {
      font-size: 1.2rem;
      font-weight: 700;
      color: #0d1b2e;
      margin-bottom: 0.6rem;
      line-height: 1.2;
    }

    @media (max-width: 480px) { .portal-card-title { font-size: 1.15rem; } }
    @media (max-width: 320px) { .portal-card-title { font-size: 1.05rem; } }

    .portal-card-desc {
      font-size: 0.88rem;
      color: #4a5568;
      line-height: 1.6;
      margin-bottom: 1.25rem;
      flex: 1;
    }

    @media (max-width: 480px) { .portal-card-desc { font-size: 0.82rem; margin-bottom: 1rem; } }
    @media (max-width: 320px) { .portal-card-desc { font-size: 0.78rem; } }

    .portal-card-arrow {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 0.88rem;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .portal-card-new:hover .portal-card-arrow { gap: 14px; }
    .portal-card-arrow i { transition: transform 0.3s ease; }
    .portal-card-new:hover .portal-card-arrow i { transform: translateX(4px); }

    @media (max-width: 480px) { .portal-card-arrow { font-size: 0.82rem; } }

    /* ==================== FEATURES SECTION ==================== */
    .features-section {
      position: relative;
      z-index: 1;
      padding: 5rem 2rem;
      touch-action: pan-y;
    }

    @media (max-width: 768px) { .features-section { padding: 3.5rem 1.25rem; } }
    @media (max-width: 480px) { .features-section { padding: 3rem 1rem; } }
    @media (max-width: 320px) { .features-section { padding: 2.5rem 0.75rem; } }

    .features-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
      max-width: 1100px;
      margin: 0 auto;
    }

    @media (max-width: 900px) {
      .features-grid { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 600px) {
      .features-grid { grid-template-columns: 1fr; gap: 1rem; }
    }

    .feature-card {
      background: #ffffff;
      border: 1px solid #dce3ed;
      border-radius: 8px;
      padding: 1.75rem 1.5rem;
      transition: all 0.3s ease;
      box-shadow: 0 2px 6px rgba(0,0,0,0.05);
    }

    .feature-card:hover {
      border-color: #003580;
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0,53,128,0.1);
    }

    @media (max-width: 480px) {
      .feature-card { padding: 1.5rem 1.25rem; border-radius: 16px; }
    }

    @media (max-width: 320px) {
      .feature-card { padding: 1.25rem 1rem; border-radius: 14px; }
    }

    .feature-icon {
      width: 48px; height: 48px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.3rem;
      margin-bottom: 1.25rem;
    }

    @media (max-width: 480px) {
      .feature-icon { width: 42px; height: 42px; font-size: 1.15rem; border-radius: 12px; margin-bottom: 1rem; }
    }

    .feature-title {
      font-size: 1.05rem;
      font-weight: 700;
      color: #0d1b2e;
      margin-bottom: 0.4rem;
    }

    @media (max-width: 480px) { .feature-title { font-size: 1rem; } }

    .feature-desc {
      font-size: 0.85rem;
      color: #4a5568;
      line-height: 1.6;
    }

    @media (max-width: 480px) { .feature-desc { font-size: 0.82rem; } }

    /* ==================== STEPS SECTION ==================== */
    .steps-section {
      position: relative;
      z-index: 1;
      padding: 5rem 2rem;
    }

    @media (max-width: 768px) { .steps-section { padding: 3.5rem 1.25rem; } }
    @media (max-width: 480px) { .steps-section { padding: 3rem 1rem; } }
    @media (max-width: 320px) { .steps-section { padding: 2.5rem 0.75rem; } }

    .step-card {
      display: flex;
      gap: 1.5rem;
      align-items: flex-start;
      padding: 1.5rem 1.75rem;
      background: #ffffff;
      border: 1px solid #dce3ed;
      border-radius: 8px;
      transition: all 0.3s ease;
      box-shadow: 0 2px 6px rgba(0,0,0,0.05);
    }

    .step-card:hover {
      border-color: #003580;
      transform: translateX(4px);
      box-shadow: 0 4px 16px rgba(0,53,128,0.08);
    }

    @media (max-width: 480px) {
      .step-card { gap: 1rem; padding: 1.25rem; border-radius: 16px; }
    }

    @media (max-width: 320px) {
      .step-card { gap: 0.75rem; padding: 1rem; border-radius: 14px; flex-direction: column; align-items: center; text-align: center; }
    }

    .step-icon {
      width: 52px; height: 52px;
      border-radius: 16px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.3rem;
    }

    @media (max-width: 480px) {
      .step-icon { width: 46px; height: 46px; font-size: 1.15rem; border-radius: 14px; }
    }

    /* ==================== CTA SECTION ==================== */
    .cta-section {
      position: relative;
      z-index: 1;
      padding: 4rem 2rem;
    }

    @media (max-width: 480px) { .cta-section { padding: 3rem 1rem; } }
    @media (max-width: 320px) { .cta-section { padding: 2.5rem 0.75rem; } }

    .cta-card {
      max-width: 700px;
      margin: 0 auto;
      background: #003580;
      border: none;
      border-top: 4px solid #c8a84b;
      border-radius: 8px;
      padding: clamp(2rem, 5vw, 3.5rem);
      text-align: center;
      position: relative;
      overflow: hidden;
      box-shadow: 0 4px 24px rgba(0,53,128,0.25);
    }

    @media (max-width: 480px) {
      .cta-card { border-radius: 20px; padding: 2rem 1.5rem; }
    }

    @media (max-width: 320px) {
      .cta-card { border-radius: 18px; padding: 1.75rem 1.25rem; }
    }

    .cta-btn {
      padding: 16px 40px;
      border-radius: 16px;
      background: linear-gradient(135deg, #003580, #1a56b0);
      color: white;
      border: none;
      font-weight: 700;
      font-size: 1.05rem;
      cursor: pointer;
      box-shadow: 0 8px 30px rgba(0,53,128,0.4);
      display: inline-flex;
      align-items: center;
      gap: 10px;
      position: relative;
      overflow: hidden;
      min-height: 52px;
      touch-action: manipulation;
      transition: all 0.3s ease;
    }

    .cta-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 40px rgba(0,53,128,0.5);
    }

    .cta-btn:active { transform: scale(0.98); }

    @media (max-width: 480px) {
      .cta-btn { padding: 14px 28px; font-size: 0.95rem; width: 100%; justify-content: center; border-radius: 14px; }
    }

    /* ==================== FOOTER ==================== */
    .home-footer {
      position: relative;
      z-index: 1;
      padding: 2rem 2rem;
      background: #0d1b2e;
      border-top: 3px solid #c8a84b;
      text-align: center;
    }

    @media (max-width: 480px) { .home-footer { padding: 2rem 1rem; } }

    .footer-divider {
      width: 60px;
      height: 3px;
      background: linear-gradient(90deg, #003580, #c8a84b);
      border-radius: 10px;
      margin: 0 auto 1.5rem;
    }

    .footer-text {
      font-size: 0.82rem;
      color: #94a3b8;
    }

    @media (max-width: 480px) { .footer-text { font-size: 0.75rem; } }

    /* ==================== INFO MARQUEE ==================== */
    .info-scroll-track {
      display: flex;
      gap: 1rem;
      overflow: hidden;
      padding: 1.5rem 0;
    }

    .info-scroll-inner {
      display: flex;
      gap: 1rem;
      animation: scrollTrack 35s linear infinite;
    }

    .info-chip {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0.65rem 1.1rem;
      background: #ffffff;
      border: 1px solid #dce3ed;
      border-radius: 6px;
      min-width: auto;
      white-space: nowrap;
      box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    }

    @media (max-width: 480px) {
      .info-chip { padding: 0.6rem 1rem; border-radius: 12px; gap: 8px; }
    }

    .info-chip-icon {
      width: 36px; height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      flex-shrink: 0;
    }

    @media (max-width: 480px) {
      .info-chip-icon { width: 32px; height: 32px; font-size: 0.9rem; border-radius: 8px; }
    }

    .info-chip-text {
      font-weight: 600;
      font-size: 0.82rem;
      color: #0d1b2e;
    }

    @media (max-width: 480px) { .info-chip-text { font-size: 0.78rem; } }

    /* ==================== MISC EFFECTS ==================== */
    .floating-orb {
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
      z-index: 0;
    }

    .morph-blob {
      position: absolute;
      animation: morphBlob 12s ease-in-out infinite;
      pointer-events: none;
      z-index: 0;
    }

    .grid-bg {
      position: absolute;
      inset: 0;
      background-image:
        linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px);
      background-size: 60px 60px;
      animation: gridPulse 8s ease-in-out infinite;
      pointer-events: none;
      z-index: 0;
      mask-image: radial-gradient(ellipse 60% 50% at 50% 50%, black 20%, transparent 70%);
      -webkit-mask-image: radial-gradient(ellipse 60% 50% at 50% 50%, black 20%, transparent 70%);
    }

    .aurora-bg {
      position: absolute;
      top: 0; left: -50%;
      width: 200%; height: 100%;
      background: linear-gradient(45deg, transparent 0%, rgba(99,102,241,0.02) 25%, rgba(6,182,212,0.03) 50%, rgba(139,92,246,0.02) 75%, transparent 100%);
      background-size: 200% 200%;
      animation: aurora 15s ease-in-out infinite;
      pointer-events: none;
      z-index: 0;
    }

    .scan-line {
      position: absolute;
      left: 0; width: 100%; height: 1px;
      background: linear-gradient(90deg, transparent, rgba(99,102,241,0.1), transparent);
      animation: scanLine 8s linear infinite;
      pointer-events: none;
      z-index: 1;
    }

    .glow-ring {
      position: absolute;
      border-radius: 50%;
      border: 2px solid;
      animation: rippleOut 3s ease-out infinite;
      pointer-events: none;
    }

    .typing-container {
      display: inline-flex;
      align-items: center;
      overflow: hidden;
    }

    .typing-cursor {
      display: inline-block;
      width: 3px;
      height: 1.2em;
      background: #6366f1;
      margin-left: 4px;
      animation: cursorBlink 0.8s step-end infinite;
      border-radius: 2px;
    }

    .particles-container {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 0;
      overflow: hidden;
    }

    .particle-dot {
      position: absolute;
      border-radius: 50%;
      background: currentColor;
    }

    .secret-zone {
      position: relative;
      cursor: default;
      user-select: none;
      -webkit-user-select: none;
    }

    /* ==================== REGISTER SUCCESS OVERLAY ==================== */
    .reg-overlay-backdrop {
      position: fixed;
      top: 0; left: 0;
      width: 100vw; height: 100vh;
      background: rgba(0, 0, 0, 0.82);
      backdrop-filter: blur(18px);
      -webkit-backdrop-filter: blur(18px);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 999999;
      padding: 20px;
    }

    .reg-overlay-card {
      background: linear-gradient(155deg, rgba(15, 23, 42, 0.97), rgba(30, 41, 59, 0.97));
      border: 2px solid rgba(16, 185, 129, 0.4);
      border-radius: 28px;
      padding: 3rem 3rem 2.5rem;
      text-align: center;
      max-width: 520px;
      width: 100%;
      box-shadow:
        0 0 100px rgba(16, 185, 129, 0.18),
        0 0 200px rgba(99, 102, 241, 0.06),
        0 30px 80px rgba(0, 0, 0, 0.55);
      position: relative;
      overflow: hidden;
    }

    @media (max-width: 480px) {
      .reg-overlay-card {
        padding: 2rem 1.5rem 1.75rem;
        border-radius: 22px;
        max-width: 95%;
      }
    }

    .reg-overlay-card::before {
      content: '';
      position: absolute;
      top: -50%; left: -50%;
      width: 200%; height: 200%;
      background: conic-gradient(
        from 0deg,
        transparent,
        rgba(16, 185, 129, 0.06),
        transparent,
        rgba(99, 102, 241, 0.04),
        transparent,
        rgba(6, 182, 212, 0.04),
        transparent
      );
      animation: regConicSpin 6s linear infinite;
    }

    @keyframes regConicSpin {
      to { transform: rotate(360deg); }
    }

    .reg-overlay-card::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: linear-gradient(90deg, transparent, #3b82f6, #6366f1, #06b6d4, transparent);
      opacity: 0.8;
      z-index: 3;
      animation: regTopShimmer 3s ease-in-out infinite;
    }

    @keyframes regTopShimmer {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }

    .reg-overlay-card > * {
      position: relative;
      z-index: 2;
    }

    .reg-icon-ring {
      width: 100px; height: 100px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.18), rgba(6, 182, 212, 0.08));
      border: 3px solid rgba(16, 185, 129, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.5rem;
      box-shadow:
        0 0 40px rgba(16, 185, 129, 0.25),
        inset 0 0 25px rgba(16, 185, 129, 0.08);
      animation: regRingPulse 2.2s ease-in-out infinite;
    }

    @media (max-width: 480px) {
      .reg-icon-ring { width: 78px; height: 78px; }
    }

    @keyframes regRingPulse {
      0%, 100% {
        box-shadow: 0 0 40px rgba(59,130,246,0.2), inset 0 0 25px rgba(59,130,246,0.08);
        transform: scale(1);
      }
      50% {
        box-shadow: 0 0 60px rgba(59,130,246,0.35), inset 0 0 35px rgba(59,130,246,0.15);
        transform: scale(1.04);
      }
    }

    .reg-icon-ring i {
      color: #3b82f6;
      font-size: 2.6rem;
      filter: drop-shadow(0 0 14px rgba(16,185,129,0.6));
    }

    @media (max-width: 480px) {
      .reg-icon-ring i { font-size: 2.1rem; }
    }

    .reg-overlay-title {
      font-size: 1.8rem;
      font-weight: 800;
      background: linear-gradient(90deg, #003580, #1a56b0, #c8a84b);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 0.5rem;
      letter-spacing: -0.5px;
    }

    @media (max-width: 480px) {
      .reg-overlay-title { font-size: 1.45rem; }
    }

    .reg-overlay-subtitle {
      color: #94a3b8;
      font-size: 0.92rem;
      line-height: 1.6;
      margin-bottom: 1.2rem;
    }

    @media (max-width: 480px) {
      .reg-overlay-subtitle { font-size: 0.84rem; }
    }

    .reg-overlay-name {
      font-size: 1.2rem;
      font-weight: 700;
      color: #e2e8f0;
      margin-bottom: 0.6rem;
      text-shadow: 0 0 20px rgba(226,232,240,0.1);
    }

    .reg-overlay-role-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.35rem 1.1rem;
      border-radius: 20px;
      font-size: 0.76rem;
      font-weight: 700;
      background: linear-gradient(135deg, rgba(99,102,241,0.15), rgba(6,182,212,0.1));
      border: 1px solid rgba(99,102,241,0.3);
      color: #a5b4fc;
      margin-bottom: 0.8rem;
      letter-spacing: 0.03em;
    }

    .reg-overlay-details {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-bottom: 0.8rem;
    }

    .reg-overlay-detail {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      font-size: 0.8rem;
      color: #94a3b8;
    }

    .reg-overlay-detail i {
      color: #3b82f6;
      font-size: 0.72rem;
      flex-shrink: 0;
    }

    .reg-overlay-detail span {
      color: #cbd5e1;
      font-weight: 600;
    }

    .reg-overlay-time {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      padding: 0.25rem 0.8rem;
      border-radius: 16px;
      font-size: 0.68rem;
      font-weight: 600;
      background: rgba(59,130,246,0.08);
      border: 1px solid rgba(59,130,246,0.18);
      color: #93c5fd;
    }

    .reg-overlay-actions {
      display: flex;
      gap: 0.75rem;
      justify-content: center;
      margin-top: 1.5rem;
      flex-wrap: wrap;
    }

    .reg-overlay-btn-main {
      background: linear-gradient(135deg, #003580, #1a56b0);
      border: none;
      color: white;
      font-size: 0.86rem;
      font-weight: 700;
      padding: 0.65rem 1.6rem;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      touch-action: manipulation;
      box-shadow: 0 0 20px rgba(0,53,128,0.25);
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      min-height: 44px;
    }

    .reg-overlay-btn-main:hover {
      transform: translateY(-2px);
      box-shadow: 0 0 30px rgba(59,130,246,0.35);
    }

    .reg-overlay-btn-main:active { transform: scale(0.97); }

    .reg-overlay-btn-home {
      background: rgba(99,102,241,0.1);
      border: 1px solid rgba(99,102,241,0.2);
      color: #a5b4fc;
      font-size: 0.84rem;
      font-weight: 600;
      padding: 0.6rem 1.4rem;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      touch-action: manipulation;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      min-height: 44px;
    }

    .reg-overlay-btn-home:hover {
      background: rgba(99,102,241,0.18);
      border-color: rgba(99,102,241,0.35);
      color: white;
    }

    .reg-overlay-btn-home:active { transform: scale(0.97); }

    .reg-overlay-bar-track {
      width: 100%;
      height: 4px;
      background: rgba(255,255,255,0.06);
      border-radius: 4px;
      margin-top: 1.5rem;
      overflow: hidden;
    }

    .reg-overlay-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #003580, #c8a84b, #1a56b0);
      border-radius: 4px;
      animation: regBarDrain 5s linear forwards;
      box-shadow: 0 0 10px rgba(59,130,246,0.3);
    }

    @keyframes regBarDrain {
      from { width: 100%; }
      to { width: 0%; }
    }

    .reg-overlay-footer {
      color: #475569;
      font-size: 0.75rem;
      margin-top: 0.6rem;
    }

    .reg-sparkle-dot {
      position: absolute;
      width: 5px; height: 5px;
      border-radius: 50%;
      animation: regSparkleAnim 2.8s ease-in-out infinite;
      z-index: 1;
    }

    .reg-sparkle-dot:nth-child(1) { top: 10%; left: 8%; background: #3b82f6; animation-delay: 0s; }
    .reg-sparkle-dot:nth-child(2) { top: 16%; right: 10%; background: #6366f1; animation-delay: 0.6s; }
    .reg-sparkle-dot:nth-child(3) { bottom: 20%; left: 14%; background: #06b6d4; animation-delay: 1.2s; }
    .reg-sparkle-dot:nth-child(4) { bottom: 10%; right: 12%; background: #a78bfa; animation-delay: 1.8s; }
    .reg-sparkle-dot:nth-child(5) { top: 48%; left: 4%; background: #60a5fa; animation-delay: 0.3s; width: 4px; height: 4px; }

    @keyframes regSparkleAnim {
      0%, 100% { opacity: 0; transform: scale(0) translateY(0) rotate(0deg); }
      50% { opacity: 1; transform: scale(1.3) translateY(-10px) rotate(180deg); }
    }

    /* ==================== REGISTER ERROR TOAST ==================== */
    .reg-error-toast {
      background: linear-gradient(135deg, rgba(239,68,68,0.1), rgba(185,28,28,0.06));
      border: 1px solid rgba(239,68,68,0.3);
      border-radius: 16px;
      padding: 1rem 1.4rem;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      box-shadow: 0 0 25px rgba(239,68,68,0.1);
      animation: regErrorShake 0.5s ease-in-out;
      position: relative;
      overflow: hidden;
    }

    .reg-error-toast::before {
      content: '';
      position: absolute;
      top: 0; left: 0;
      width: 4px; height: 100%;
      background: #ef4444;
      box-shadow: 0 0 12px rgba(239,68,68,0.5);
    }

    @keyframes regErrorShake {
      0%, 100% { transform: translateX(0); }
      15% { transform: translateX(-6px); }
      35% { transform: translateX(6px); }
      55% { transform: translateX(-4px); }
      75% { transform: translateX(4px); }
    }

    .reg-error-icon {
      color: #ef4444;
      font-size: 1.3rem;
      flex-shrink: 0;
      filter: drop-shadow(0 0 6px rgba(239,68,68,0.4));
    }

    .reg-error-text {
      color: #fca5a5;
      font-size: 0.9rem;
      font-weight: 500;
      flex: 1;
    }

    .reg-error-close {
      background: none;
      border: none;
      color: #ef4444;
      font-size: 1.1rem;
      cursor: pointer;
      opacity: 0.6;
      transition: opacity 0.2s;
      padding: 4px;
      touch-action: manipulation;
      flex-shrink: 0;
    }

    .reg-error-close:hover { opacity: 1; }

    /* ==================== SAFE AREAS ==================== */
    @supports (padding: env(safe-area-inset-top)) {
      .page-center {
        padding-top: calc(2rem + env(safe-area-inset-top));
        padding-bottom: calc(2rem + env(safe-area-inset-bottom));
        padding-left: calc(1rem + env(safe-area-inset-left));
        padding-right: calc(1rem + env(safe-area-inset-right));
      }
      .home-navbar {
        padding-top: calc(0.75rem + env(safe-area-inset-top));
        padding-left: calc(1rem + env(safe-area-inset-left));
        padding-right: calc(1rem + env(safe-area-inset-right));
      }
      .home-footer {
        padding-bottom: calc(2rem + env(safe-area-inset-bottom));
      }
    }

    /* ==================== SCROLLBAR ==================== */
    ::-webkit-scrollbar { width: 5px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(99, 102, 241, 0.2); border-radius: 10px; }
    ::-webkit-scrollbar-thumb:hover { background: rgba(99, 102, 241, 0.35); }

    /* ==================== REDUCED MOTION ==================== */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }

    /* ==================== TOUCH INTERACTIONS ==================== */
    @media (hover: none) and (pointer: coarse) {
      .portal-card-new:active { transform: translateY(-2px) scale(0.97) !important; }
      .feature-card:active { transform: scale(0.97) !important; }
      .stat-card:active { transform: scale(0.97) !important; }
      button:active { opacity: 0.85; }
    }

    @media (hover: hover) {
      .portal-card-new:hover { box-shadow: 0 20px 60px rgba(0,0,0,0.25), 0 0 40px rgba(99,102,241,0.1); }
    }

    /* ==================== LANDSCAPE PHONE ==================== */
    @media (max-height: 500px) and (orientation: landscape) {
      .hero-section { min-height: auto; padding: 4.5rem 2rem 2rem; }
      .stats-section { padding: 2rem 1rem; }
      .portals-section { padding: 2rem 1rem; }
    }

    /* ==================== LARGE SCREENS ==================== */
    @media (min-width: 1440px) {
      .portals-grid { max-width: 1200px; }
      .features-grid { max-width: 1200px; }
    }
  `}</style>
);

// ===================== REUSABLE FORM INPUT =====================
const ModernInput = ({
  label, icon, type = "text", name, value, onChange, error,
  isPassword, showPassword, togglePassword,
  isNida, nidaValidation, nidaPrefix, nidaCount, validationStatus, helperText,
  ...props
}) => {
  const getInputClass = () => {
    let cls = "modern-input";
    if (error || (nidaValidation && nidaValidation.error)) cls += " input-error";
    else if (validationStatus === "valid") cls += " input-valid";
    return cls;
  };

  return (
    <div className="mb-4 position-relative">
      <label className="form-label text-white fw-bold mb-2 d-block" style={{ fontSize: "0.95rem" }}>
        {label}
        {isNida && nidaPrefix && (
          <span className="nida-badge ms-2" style={{
            display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 10px",
            borderRadius: "8px", fontSize: "0.68rem", fontWeight: "700",
            background: `${nidaPrefix.color}18`, color: nidaPrefix.color,
            border: `1px solid ${nidaPrefix.color}30`, verticalAlign: "middle",
          }}>
            <i className={`bi ${nidaPrefix.color === "#ef4444" ? "bi-x-circle" : "bi-shield-check"}`} style={{ fontSize: "0.6rem" }} />
            {nidaPrefix.label}
          </span>
        )}
        {isNida && nidaCount && (
          <span className="nida-count ms-auto float-end" style={{
            background: nidaCount.isComplete ? "rgba(16,185,129,0.12)" : "rgba(99,102,241,0.1)",
            color: nidaCount.isComplete ? "#3b82f6" : "#94a3b8",
            border: `1px solid ${nidaCount.isComplete ? "rgba(59,130,246,0.15)" : "rgba(99,102,241,0.15)"}`,
          }}>
            {nidaCount.current}/{nidaCount.total}
          </span>
        )}
      </label>
      <div className="position-relative">
        <input type={type} name={name} value={value} onChange={onChange} className={getInputClass()}
          style={isNida ? { fontFamily: "'JetBrains Mono', 'Fira Code', monospace", letterSpacing: "0.08em" } : {}} {...props} />
        <i className={`bi ${icon} input-icon`} style={{
          color: error || (nidaValidation && nidaValidation.error) ? "#ef4444" : validationStatus === "valid" ? "#3b82f6" : undefined,
        }} />
        {!isPassword && validationStatus === "valid" && <i className="bi bi-check-circle-fill input-status-icon" style={{ color: "#3b82f6" }} />}
        {!isPassword && (error || (nidaValidation && nidaValidation.error)) && <i className="bi bi-exclamation-circle-fill input-status-icon" style={{ color: "#ef4444" }} />}
        {isPassword && (
          <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`} onClick={togglePassword}
            style={{ position: "absolute", right: "1.5rem", top: "50%", transform: "translateY(-50%)",
              cursor: "pointer", color: "var(--text-muted)", fontSize: "1.3rem", zIndex: 10, touchAction: "manipulation",
              padding: "8px", minWidth: "44px", minHeight: "44px", display: "flex", alignItems: "center", justifyContent: "center" }} />
        )}
      </div>
      {isNida && nidaCount && nidaCount.current > 0 && (
        <div className="nida-progress-bar">
          <div className="nida-progress-fill" style={{
            width: `${nidaCount.percentage}%`,
            background: nidaCount.isComplete ? "linear-gradient(90deg, #3b82f6, #06b6d4)" : nidaValidation?.error ? "linear-gradient(90deg, #ef4444, #f59e0b)" : "linear-gradient(90deg, #6366f1, #06b6d4)",
          }} />
        </div>
      )}
      {error && (<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="error-text text-danger mt-1 small"><Icon.ExclamationTriangleFill className="me-1" /> {error}</motion.div>)}
      {isNida && nidaValidation && nidaValidation.error && !error && (
        <div className="nida-error-msg mt-2" style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.78rem", fontWeight: "600", color: "#ef4444", padding: "6px 10px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.12)", borderRadius: "10px" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "0.72rem" }} />{nidaValidation.error}
        </div>
      )}
      {isNida && helperText && !nidaValidation?.error && !error && (
        <div className="nida-badge mt-2" style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.72rem", color: "#94a3b8", padding: "6px 10px", background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.08)", borderRadius: "10px" }}>
          <i className="bi bi-info-circle" style={{ fontSize: "0.68rem", color: "#6366f1" }} />{helperText}
        </div>
      )}
    </div>
  );
};

// ===================== ROLE SELECTION PAGE =====================
const RoleSelectionPage = ({ mode }) => {
  const navigate = useNavigate();

  const roles = [
    { title: "Akagari", icon: "bi-bank2", route: "/akagari", color: "#003580", gradient: "linear-gradient(135deg, #003580, #1a56b0)", description: "Sisitemu y'abayobozi b'Akagari", emoji: "🏛️" },
    { title: "Umudugudu", icon: "bi-people-fill", route: "/mudugudu", color: "#1a56b0", gradient: "linear-gradient(135deg, #1a56b0, #0369a1)", description: "Urubuga rw'umuyobozi w'umudugudu", emoji: "👥" },
    { title: "Isibo / Miberehomyiza", icon: "bi-grid-3x3-gap-fill", route: "/abandi", color: "#1a7a4a", gradient: "linear-gradient(135deg, #1a7a4a, #0d5c38)", description: "Urubuga rw'abayobozi b'Isibo", emoji: "🌐" },
    { title: "Umutekano", icon: "bi-shield-lock-fill", route: "/umutekano-login", color: "#b91c1c", gradient: "linear-gradient(135deg, #b91c1c, #7f1d1d)", description: "Sisitemu y'Umutekano n'Ubutabazi", emoji: "🛡️" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #001233 0%, #003580 60%, #001f4d 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem 1rem", position: "relative", overflow: "hidden" }}>
      {/* Gold top border */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: "#c8a84b" }} />

      {/* Back button */}
      <button onClick={() => navigate("/")} style={{ position: "absolute", top: "1.5rem", left: "1.5rem", background: "transparent", border: "1px solid rgba(200,168,75,0.3)", color: "#c8a84b", borderRadius: "8px", padding: "8px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.85rem", fontWeight: "600", minHeight: "40px" }}>
        <i className="bi bi-arrow-left" /> Gusubira
      </button>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "5px 16px", borderRadius: "4px", background: "rgba(200,168,75,0.15)", border: "1px solid rgba(200,168,75,0.45)", color: "#c8a84b", fontSize: "0.72rem", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "1rem" }}>
          <i className="bi bi-bank2" style={{ fontSize: "0.78rem" }} />
          Repubulika y'u Rwanda
        </div>
        <h1 style={{ fontSize: "clamp(1.6rem, 5vw, 2.8rem)", fontWeight: "800", color: "#ffffff", marginBottom: "0.5rem", letterSpacing: "-0.02em" }}>
          Hitamo Urubuga
        </h1>
        <div style={{ width: "48px", height: "3px", background: "#c8a84b", borderRadius: "2px", margin: "0 auto 0.75rem" }} />
        <p style={{ color: "rgba(226,232,240,0.7)", fontSize: "clamp(0.85rem, 2vw, 1rem)", maxWidth: "420px" }}>
          Hitamo inzego z'ibanze ukwiye kwinjiramo
        </p>
      </div>

      {/* Role cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.25rem", width: "100%", maxWidth: "900px" }}>
        {roles.map((r, i) => (
          <button key={i} onClick={() => navigate(r.route)}
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid rgba(255,255,255,0.1)`, borderLeft: `4px solid ${r.color}`, borderRadius: "10px", padding: "1.75rem 1.5rem", cursor: "pointer", textAlign: "left", transition: "all 0.25s ease", display: "flex", flexDirection: "column", gap: "0.75rem", minHeight: "160px" }}
            onMouseEnter={e => { e.currentTarget.style.background = `rgba(255,255,255,0.08)`; e.currentTarget.style.borderLeftColor = "#c8a84b"; e.currentTarget.style.transform = "translateY(-3px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = `rgba(255,255,255,0.04)`; e.currentTarget.style.borderLeftColor = r.color; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: `${r.color}20`, border: `1px solid ${r.color}40`, display: "flex", alignItems: "center", justifyContent: "center", color: r.color, fontSize: "1.3rem", flexShrink: 0 }}>
                <i className={`bi ${r.icon}`} />
              </div>
              <div>
                <div style={{ fontSize: "1.1rem", fontWeight: "700", color: "#ffffff", lineHeight: 1.2 }}>{r.title}</div>
                <div style={{ fontSize: "0.8rem", color: "rgba(226,232,240,0.6)", marginTop: "2px" }}>{r.description}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#c8a84b", fontSize: "0.82rem", fontWeight: "600", marginTop: "auto" }}>
              {"Injira"} <i className="bi bi-arrow-right" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const RoleDashboard = () => {
  const { role } = useParams();
  switch (role) {
    case "Akagari": return <DashboardBohejuru />;
    case "Mudugudu": return <Dashboard />;
    case "Isibo": return <DashboardIsibo />;
    case "Umunyamabanga": return <DashboardUmunyamabanga />;
    case "ROLE_UMUTEKANO": return <UmutekanoDashboard />;
    default: return <div className="text-center text-white py-5">Role Not Found</div>;
  }
};

// ===================== LOADING SCREEN =====================
const LoadingScreen = ({ onComplete }) => {
  useEffect(() => { const timer = setTimeout(onComplete, 2000); return () => clearTimeout(timer); }, [onComplete]);
  return (
    <div className="page-center bg-dark text-white">
      <div className="text-center">
        <div className="spinner-border text-primary mb-3"></div>
        <h3>Urubuga rw'Umudugudu...</h3>
      </div>
    </div>
  );
};

// ===================== ANIMATED COUNTER HOOK =====================
const useAnimatedCounter = (target, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!shouldStart || hasStarted) return;
    setHasStarted(true);
    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration, shouldStart, hasStarted]);

  return count;
};

// ===================== FLOATING ORB =====================
const FloatingOrb = ({ size, color, top, left, delay, duration }) => (
  <motion.div className="floating-orb"
    style={{ width: size, height: size, background: `radial-gradient(circle, ${color}30, ${color}08, transparent)`, top, left }}
    animate={{ y: [0, -30, 0, 20, 0], x: [0, 15, -10, 5, 0], scale: [1, 1.12, 0.95, 1.06, 1] }}
    transition={{ duration: duration || 8, repeat: Infinity, ease: "easeInOut", delay: delay || 0 }}
  />
);

// ===================== PARTICLE FIELD =====================
const ParticleField = ({ count = 30 }) => {
  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 15 + 10,
      delay: Math.random() * 10,
      color: ["#6366f1", "#06b6d4", "#818cf8", "#a5b4fc", "#3b82f6"][Math.floor(Math.random() * 5)],
    }));
  }, [count]);

  return (
    <div className="particles-container">
      {particles.map((p) => (
        <motion.div key={p.id} className="particle-dot"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, color: p.color, opacity: 0 }}
          animate={{ y: [0, -(Math.random() * 150 + 50), 0], x: [0, (Math.random() - 0.5) * 80, 0], opacity: [0, 0.5, 0], scale: [0.5, 1.5, 0.5] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
};

// ===================== MORPH BLOB =====================
const MorphBlob = ({ size, color, top, left, delay }) => (
  <div className="morph-blob"
    style={{ width: size, height: size, background: `radial-gradient(ellipse at center, ${color}15, ${color}04, transparent)`, top, left, animationDelay: `${delay || 0}s` }}
  />
);

// ===================== TYPING TEXT =====================
const TypingText = ({ texts, speed = 80, pause = 2000 }) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentFullText = texts[currentTextIndex];
    let timeout;
    if (!isDeleting && displayText === currentFullText) {
      timeout = setTimeout(() => setIsDeleting(true), pause);
    } else if (isDeleting && displayText === "") {
      setIsDeleting(false);
      setCurrentTextIndex((prev) => (prev + 1) % texts.length);
    } else {
      timeout = setTimeout(() => {
        setDisplayText(isDeleting ? currentFullText.substring(0, displayText.length - 1) : currentFullText.substring(0, displayText.length + 1));
      }, isDeleting ? speed / 2 : speed);
    }
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentTextIndex, texts, speed, pause]);

  return (
    <span className="typing-container">
      <span>{displayText}</span>
      <span className="typing-cursor" />
    </span>
  );
};

// ===================== MAGNETIC ELEMENT =====================
const MagneticElement = ({ children, strength = 0.3 }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    if (!ref.current || window.innerWidth < 768) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    setPosition({ x: (e.clientX - centerX) * strength, y: (e.clientY - centerY) * strength });
  }, [strength]);

  const handleMouseLeave = useCallback(() => { setPosition({ x: 0, y: 0 }); }, []);

  return (
    <motion.div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}
      animate={{ x: position.x, y: position.y }}
      transition={{ type: "spring", stiffness: 350, damping: 15, mass: 0.2 }}
      style={{ display: "inline-block" }}>
      {children}
    </motion.div>
  );
};

// ===================== SCROLL PROGRESS BAR =====================
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  return (
    <motion.div style={{
      scaleX, position: "fixed", top: 0, left: 0, right: 0, height: "3px",
      background: "linear-gradient(90deg, #003580, #c8a84b, #1a56b0)",
      transformOrigin: "0%", zIndex: 9999,
    }} />
  );
};

// ===================== REVEAL ON SCROLL =====================
const RevealOnScroll = ({ children, direction = "up", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  const variants = {
    hidden: {
      opacity: 0,
      y: direction === "up" ? 50 : direction === "down" ? -50 : 0,
      x: direction === "left" ? 50 : direction === "right" ? -50 : 0,
      scale: 0.96,
    },
    visible: {
      opacity: 1, y: 0, x: 0, scale: 1,
      transition: { duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] },
    },
  };

  return (
    <motion.div ref={ref} variants={variants} initial="hidden" animate={isInView ? "visible" : "hidden"}>
      {children}
    </motion.div>
  );
};

// ===================== INFO MARQUEE =====================
const InfoMarquee = () => {
  const items = [
    { icon: "bi-shield-check", text: "Kubika amakuru wizewe", color: "#003580" },
    { icon: "bi-lightning-charge", text: "Byihuse cyane", color: "#c8a84b" },
    { icon: "bi-phone", text: "Ukoreshe telefone", color: "#1a56b0" },
    { icon: "bi-cloud-check", text: "Andika raporo", color: "#1a7a4a" },
    { icon: "bi-graph-up-arrow", text: "Ohereza raporo", color: "#003580" },
    { icon: "bi-people", text: "Gukorana byoroshye", color: "#c8a84b" },
    { icon: "bi-lock", text: "Ohereza ubutumwa", color: "#1a56b0" },
    { icon: "bi-check-circle", text: "NIDA Validation", color: "#1a7a4a" },
  ];

  const doubled = [...items, ...items];

  return (
    <div style={{ overflow: "hidden", padding: "1.5rem 0", position: "relative", zIndex: 1 }}>
      <div className="info-scroll-track">
        <div className="info-scroll-inner">
          {doubled.map((item, i) => (
            <div key={i} className="info-chip">
              <div className="info-chip-icon" style={{
                background: `${item.color}12`, border: `1px solid ${item.color}20`,
                color: item.color,
              }}>
                <i className={`bi ${item.icon}`} />
              </div>
              <span className="info-chip-text">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===================== 3D TILT CARD =====================
const TiltCard = ({ children, className, style, onClick, onMouseEnter, onMouseLeave }) => {
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current || window.innerWidth < 768) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * -8, y: x * 8 });
  }, []);

  const handleLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    if (onMouseLeave) onMouseLeave();
  }, [onMouseLeave]);

  return (
    <motion.div ref={cardRef} className={className}
      style={{ ...style, perspective: "1000px", transformStyle: "preserve-3d" }}
      animate={{ rotateX: tilt.x, rotateY: tilt.y }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      onMouseMove={handleMouseMove} onMouseLeave={handleLeave}
      onMouseEnter={onMouseEnter} onClick={onClick}>
      {children}
    </motion.div>
  );
};

// ===================== LANDING PAGE =====================
const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [secretTapCount, setSecretTapCount] = useState(0);

  const handleSecretTap = useCallback(() => {
    const newCount = secretTapCount + 1;
    setSecretTapCount(newCount);
    if (newCount >= 5) { setSecretTapCount(0); }
    setTimeout(() => setSecretTapCount(0), 3000);
  }, [secretTapCount]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToPortals = () => {};

  return (
    <div className="home-page">
      <ScrollProgress />

      {/* ==================== NAVBAR ==================== */}
      <motion.nav className={`home-navbar ${scrolled ? "scrolled" : ""}`}
        initial={{ y: -100, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}>

        <div className="nav-logo secret-zone" onClick={handleSecretTap}>
          <IgitaboLogo size={56} variant="full" />
          {secretTapCount > 0 && secretTapCount < 5 && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
              style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#6366f1", marginLeft: "4px", boxShadow: `0 0 ${secretTapCount * 3}px rgba(99,102,241,0.5)` }}
            />
          )}
        </div>
      </motion.nav>

      {/* ==================== HERO CAROUSEL ==================== */}
      <HeroCarousel onScrollDown={scrollToPortals} />



      {/* ==================== FOOTER ==================== */}
      <footer className="home-footer">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "0.75rem" }}>
          <IgitaboLogo size={32} variant="full" />
        </div>
        <p className="footer-text">© {new Date().getFullYear()} Inkingi — Sisitemu y'Umudugudu Dijitali</p>

      </footer>
    </div>
  );
};

// ===================== SUPER ADMIN LOGIN =====================
const SuperAdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSuperLogin = async (e) => {
    e.preventDefault(); setIsLoading(true);
    try {
      const res = await axiosInstance.post("/login_super_admin", { username, password });
      localStorage.setItem("superToken", res.data.token); navigate("/sys-root/dashboard");
    } catch (err) { setError("Access Denied"); } finally { setIsLoading(false); }
  };

  return (
    <div className="page-center">
      <div className="form-container" style={{ borderColor: "rgba(239, 68, 68, 0.25)" }}>
        <button className="btn-back" onClick={() => navigate("/")}><i className="bi bi-x-lg me-2"></i> Close</button>
        <h2 className="form-title" style={{ background: "linear-gradient(90deg, #ef4444, #f59e0b)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>System Auth</h2>
        <form onSubmit={handleSuperLogin}>
          <ModernInput label="ID" icon="bi-shield-lock" placeholder="System ID" value={username} onChange={(e) => setUsername(e.target.value)} />
          <ModernInput label="Key" icon="bi-key" type={showPassword ? "text" : "password"} placeholder="Security Key" value={password} onChange={(e) => setPassword(e.target.value)} isPassword showPassword={showPassword} togglePassword={() => setShowPassword(!showPassword)} />
          {error && <div className="text-danger small mb-3 text-center">{error}</div>}
          <button type="submit" className="btn-gradient" style={{ background: "linear-gradient(135deg, #ef4444, #b91c1c)" }} disabled={isLoading}>{isLoading ? "Verifying..." : "Authenticate"}</button>
        </form>
      </div>
    </div>
  );
};

// ===================== LOGIN FORM =====================
const LoginForm = ({ title, apiEndpoint, redirectPath, hideTitle }) => {
  const [national_id, setNationalId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [nidaTouched, setNidaTouched] = useState(false);
  const navigate = useNavigate();

  const nidaValidation = NidaValidator.validate(national_id);
  const nidaPrefix = NidaValidator.getPrefix(national_id);
  const nidaCount = NidaValidator.getCountDisplay(national_id);

  const getValidationStatus = () => {
    if (!national_id) return null;
    if (nidaValidation && nidaValidation.valid) return "valid";
    if (nidaTouched && nidaValidation && nidaValidation.error) return "error";
    return "typing";
  };

  const handleNidaChange = (e) => { const formatted = NidaValidator.format(e.target.value); setNationalId(formatted); if (errors.general) setErrors({}); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setNidaTouched(true);
    const validation = NidaValidator.validate(national_id);
    if (!national_id.trim()) { setErrors({ nida: "Indangamuntu irakenewe" }); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); return; }
    if (validation && !validation.valid && validation.error) { setErrors({ nida: validation.error }); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); return; }
    if (!password.trim()) { setErrors({ password: "Ijambo ry'ibanga rirakenewe" }); return; }
    setIsLoading(true); setErrors({});
    try {
      const res = await axiosInstance.post(apiEndpoint, { national_id: NidaValidator.clean(national_id), password });
      localStorage.setItem("token", res.data.token);
      if (res.data.user_id) localStorage.setItem("user_id", String(res.data.user_id));
      if (res.data.sup_id) localStorage.setItem("sup_id", String(res.data.sup_id));
      if (res.data.role) localStorage.setItem("role", res.data.role);
      if (res.data.fullname) localStorage.setItem("fullname", res.data.fullname);
      const finalPath = redirectPath.includes(":role") ? redirectPath.replace(":role", res.data.role || "Isibo") : redirectPath;
      if (navigator.vibrate) navigator.vibrate(50); navigate(finalPath);
    } catch (err) { const msg = err.response?.data?.error; setErrors({ general: typeof msg === 'string' ? msg : "Kwinjira ntibyakunze" }); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); } finally { setIsLoading(false); }
  };

  const isSubmitDisabled = isLoading || !national_id.trim() || !password.trim() || (nidaValidation && !nidaValidation.valid && nidaValidation.error);

  return (
    <div className={hideTitle ? "" : "page-center"}>
      <div className={hideTitle ? "" : "form-container"}>
        {!hideTitle && <button className="btn-back" onClick={() => navigate("/")}><i className="bi bi-arrow-left me-2"></i> Gusubira Inyuma</button>}
        {!hideTitle && <h2 className="form-title">{title}</h2>}
        {hideTitle && <button className="btn-back" onClick={() => navigate("/")} style={{marginBottom:"1.5rem"}}><i className="bi bi-arrow-left me-2"></i> Gusubira Inyuma</button>}
        <form onSubmit={handleSubmit}>
          <ModernInput label="Indangamuntu (16)" icon="bi-card-text" placeholder="119 cyangwa 120..." value={national_id} onChange={handleNidaChange} onBlur={() => setNidaTouched(true)} error={errors.nida} maxLength={22} inputMode="numeric" autoComplete="off" isNida nidaValidation={nidaTouched ? nidaValidation : null} nidaPrefix={nidaPrefix} nidaCount={nidaCount} validationStatus={getValidationStatus()} helperText={!national_id ? "Tangira na 119 cyangwa 120 — Imibare 16" : null} />
          <ModernInput label="Ijambo ry'ibanga" icon="bi-lock" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((prev) => ({ ...prev, password: undefined })); }} error={errors.password} isPassword showPassword={showPassword} togglePassword={() => setShowPassword(!showPassword)} />
          {errors.general && (<motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="text-danger small mb-3" style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.12)", borderRadius: "12px" }}><Icon.ExclamationTriangleFill className="me-1" />{errors.general}</motion.div>)}
          <button type="submit" className="btn-gradient" disabled={isSubmitDisabled}>
            {isLoading ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Tegereza...</>) : (<><i className="bi bi-box-arrow-in-right me-2"></i>Injira</>)}
          </button>
          {nidaValidation && nidaValidation.valid && (<motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ marginTop: "12px", padding: "10px 14px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: "12px", display: "flex", alignItems: "center", gap: "8px", fontSize: "0.8rem", color: "#3b82f6", fontWeight: "600" }}><i className="bi bi-shield-fill-check" style={{ fontSize: "0.9rem" }} />Indangamuntu yujujwe neza ({nidaPrefix?.label})</motion.div>)}
        </form>
      </div>
    </div>
  );
};

// ===================== AKAGARI LOGIN PAGE =====================
const AkagariLoginPage = () => (
  <div className="akagari-login-page">
    <div className="akagari-login-card">
      <div className="text-center">
        <div className="akagari-seal"><i className="bi bi-bank2"></i></div>
        <h1 className="akagari-login-title">Akagari</h1>
      </div>
      <LoginForm apiEndpoint="/login_akagari" redirectPath="/dashboard/Akagari" hideTitle />
    </div>
  </div>
);

// ===================== REGISTER FORM =====================
const Register = () => {
  const [formData, setFormData] = useState({ national_id: "", fullname: "", email: "", telefone: "", password: "", confirmPassword: "", role: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [nidaTouched, setNidaTouched] = useState(false);
  const navigate = useNavigate();

  // ADDED: Success overlay and error message state
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [apiError, setApiError] = useState("");

  const nidaValidation = NidaValidator.validate(formData.national_id);
  const nidaPrefix = NidaValidator.getPrefix(formData.national_id);
  const nidaCount = NidaValidator.getCountDisplay(formData.national_id);

  const getRegNidaStatus = () => { if (!formData.national_id) return null; if (nidaValidation && nidaValidation.valid) return "valid"; if (nidaTouched && nidaValidation && nidaValidation.error) return "error"; return "typing"; };
  const handleRegNidaChange = (e) => { const formatted = NidaValidator.format(e.target.value); setFormData({ ...formData, national_id: formatted }); if (errors.nida) setErrors((prev) => ({ ...prev, nida: undefined })); };

  // ADDED: Close overlay and go home
  const handleGoHome = () => {
    setShowSuccessOverlay(false);
    setSuccessData(null);
    navigate("/");
  };

  // ADDED: Close overlay and register another
  const handleRegisterAnother = () => {
    setShowSuccessOverlay(false);
    setSuccessData(null);
  };

  // ADDED: Dismiss error
  const dismissError = () => {
    setApiError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setNidaTouched(true);
    setApiError("");
    const validation = NidaValidator.validate(formData.national_id);
    const newErrors = {};
    if (!formData.national_id.trim()) newErrors.nida = "Indangamuntu irakenewe";
    else if (validation && !validation.valid && validation.error) newErrors.nida = validation.error;
    if (!formData.fullname.trim()) newErrors.fullname = "Amazina arakenewe";
    if (!formData.role) newErrors.role = "Hitamo inshingano";
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); if (navigator.vibrate) navigator.vibrate([100, 50, 100]); return; }
    setIsLoading(true); setErrors({});
    try {
      const submitData = { ...formData, national_id: NidaValidator.clean(formData.national_id) };
      await axiosInstance.post("/register_abandi", submitData);
      if (navigator.vibrate) navigator.vibrate(50);

      // ADDED: Save success data and show overlay
      setSuccessData({
        fullname: formData.fullname,
        national_id: formData.national_id,
        role: formData.role,
        email: formData.email || "—",
        telefone: formData.telefone || "—",
        timestamp: new Date().toLocaleTimeString("rw-RW", { hour: "2-digit", minute: "2-digit" }),
        date: new Date().toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" }),
      });
      setShowSuccessOverlay(true);

      // ADDED: Auto-close after 5.5s
      setTimeout(() => {
        setShowSuccessOverlay(false);
      }, 5500);

      setFormData({ national_id: "", fullname: "", email: "", telefone: "", password: "", confirmPassword: "", role: "" });
      setNidaTouched(false);

    } catch (err) {
      // ADDED: Show styled error instead of generic
      const errorMsg = err.response?.data?.error || "Habaye ikosa mu kwiyandikisha. Ongera ugerageze.";
      setApiError(errorMsg);
      setErrors({ general: errorMsg });
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      setTimeout(() => setApiError(""), 8000);
    } finally { setIsLoading(false); }
  };

  return (
    <div className="page-center">

      {/* ── ADDED: Full-Screen Success Overlay ── */}
      <AnimatePresence>
        {showSuccessOverlay && successData && (
          <motion.div
            className="reg-overlay-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
          >
            <motion.div
              className="reg-overlay-card"
              initial={{ scale: 0.35, opacity: 0, rotateX: 30, y: 40 }}
              animate={{ scale: 1, opacity: 1, rotateX: 0, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 30 }}
              transition={{ type: "spring", stiffness: 170, damping: 18, delay: 0.1 }}
            >
              {/* Sparkle particles */}
              <div className="reg-sparkle-dot"></div>
              <div className="reg-sparkle-dot"></div>
              <div className="reg-sparkle-dot"></div>
              <div className="reg-sparkle-dot"></div>
              <div className="reg-sparkle-dot"></div>

              {/* Pulsing icon ring */}
              <motion.div
                className="reg-icon-ring"
                initial={{ scale: 0, rotate: -200 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 13, delay: 0.3 }}
              >
                <motion.i
                  className="bi bi-check-circle-fill"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
                />
              </motion.div>

              {/* Title */}
              <motion.h2
                className="reg-overlay-title"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
              >
                Byagenze Neza!
              </motion.h2>

              {/* Subtitle */}
              <motion.p
                className="reg-overlay-subtitle"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                Umuyobozi w'Akagari yanditswe neza muri sisitemu.
              </motion.p>

              {/* User name */}
              <motion.div
                className="reg-overlay-name"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                <i className="bi bi-person-badge me-2" style={{ color: "#3b82f6" }} />
                {successData.fullname}
              </motion.div>

              {/* Role pill */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.95 }}
              >
                <div className="reg-overlay-role-pill">
                  <i className="bi bi-briefcase-fill" style={{ fontSize: "0.7rem" }} />
                  {successData.role}
                </div>
              </motion.div>

              {/* Details */}
              <motion.div
                className="reg-overlay-details"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05 }}
              >
                <div className="reg-overlay-detail">
                  <i className="bi bi-fingerprint" />
                  <span>{successData.national_id}</span>
                </div>
                {successData.email !== "—" && (
                  <div className="reg-overlay-detail">
                    <i className="bi bi-envelope-fill" />
                    <span>{successData.email}</span>
                  </div>
                )}
                {successData.telefone !== "—" && (
                  <div className="reg-overlay-detail">
                    <i className="bi bi-telephone-fill" />
                    <span>{successData.telefone}</span>
                  </div>
                )}
              </motion.div>

              {/* Timestamp */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.15 }}
              >
                <div className="reg-overlay-time">
                  <i className="bi bi-clock" style={{ fontSize: "0.6rem" }} />
                  {successData.date} — {successData.timestamp}
                </div>
              </motion.div>

              {/* Action buttons */}
              <motion.div
                className="reg-overlay-actions"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
              >
                <button type="button" className="reg-overlay-btn-main" onClick={handleRegisterAnother}>
                  <i className="bi bi-plus-circle" style={{ fontSize: "0.85rem" }} />
                  Andikisha Undi
                </button>
                <button type="button" className="reg-overlay-btn-home" onClick={handleGoHome}>
                  <i className="bi bi-house" style={{ fontSize: "0.8rem" }} />
                  Ahabanza
                </button>
              </motion.div>

              {/* Countdown bar */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4 }}
              >
                <div className="reg-overlay-bar-track">
                  <div className="reg-overlay-bar-fill"></div>
                </div>
                <p className="reg-overlay-footer">
                  <i className="bi bi-arrow-repeat me-1" />
                  Ifungura mu masegonda 5...
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="form-container">
        <button className="btn-back" onClick={() => navigate("/")}><i className="bi bi-arrow-left me-2"></i> Gusubira Inyuma</button>
        <h2 className="form-title">Iyandikishe</h2>

        {/* ── ADDED: Styled Error Toast ── */}
        <AnimatePresence>
          {apiError && (
            <motion.div
              className="reg-error-toast"
              initial={{ opacity: 0, y: -12, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.95 }}
              transition={{ duration: 0.35 }}
            >
              <i className="bi bi-exclamation-triangle-fill reg-error-icon"></i>
              <span className="reg-error-text">{apiError}</span>
              <button className="reg-error-close" onClick={dismissError} type="button">
                <i className="bi bi-x-lg"></i>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-6">
            <ModernInput label="Indangamuntu (16)" icon="bi-card-list" placeholder="119 cyangwa 120..." value={formData.national_id} onChange={handleRegNidaChange} onBlur={() => setNidaTouched(true)} error={errors.nida} maxLength={22} inputMode="numeric" autoComplete="off" isNida nidaValidation={nidaTouched ? nidaValidation : null} nidaPrefix={nidaPrefix} nidaCount={nidaCount} validationStatus={getRegNidaStatus()} helperText={!formData.national_id ? "Tangira na 119 cyangwa 120" : null} />
          </div>
          <div className="col-md-6">
            <ModernInput label="Amazina" icon="bi-person" value={formData.fullname} onChange={(e) => setFormData({ ...formData, fullname: e.target.value })} error={errors.fullname} placeholder="Amazina yawe..." />
          </div>
          <div className="col-md-6">
            <ModernInput label="Imeyili" icon="bi-envelope" type="email" placeholder="imeyili@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
          </div>
          <div className="col-md-6">
            <ModernInput label="Telefone" icon="bi-telephone" placeholder="07XXXXXXXX" value={formData.telefone} onChange={(e) => setFormData({ ...formData, telefone: e.target.value })} />
          </div>
          <div className="col-md-6">
            <ModernInput label="Ijambo ry'ibanga" icon="bi-lock" type={formData.showPwd ? "text" : "password"} placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} isPassword showPassword={formData.showPwd} togglePassword={() => setFormData((p) => ({ ...p, showPwd: !p.showPwd }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label text-white fw-bold mb-2 d-block" style={{ fontSize: "0.95rem" }}>
              Hitamo inshingano
              {errors.role && (<span className="text-danger ms-2" style={{ fontSize: "0.78rem", fontWeight: "600" }}><i className="bi bi-exclamation-triangle-fill me-1" style={{ fontSize: "0.7rem" }} />{errors.role}</span>)}
            </label>
            <select className={`modern-input ${errors.role ? "input-error" : ""}`} style={{ paddingLeft: "1.25rem", cursor: "pointer" }} onChange={(e) => { setFormData({ ...formData, role: e.target.value }); if (errors.role) setErrors((prev) => ({ ...prev, role: undefined })); }} value={formData.role}>
              <option value="">Hitamo inshingano...</option>
              <option value="Isibo">Isibo</option>
              <option value="Umunyamabanga">Miberehomyiza</option>
            </select>
          </div>
          {errors.general && !apiError && (<div className="text-danger small col-12"><div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.12)", borderRadius: "12px" }}><Icon.ExclamationTriangleFill className="me-2" />{errors.general}</div></div>)}
          <div className="col-12">
            <button type="submit" className="btn-gradient" disabled={isLoading}>
              {isLoading ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Tegereza...</>) : (<><i className="bi bi-person-plus me-2"></i>Iyandikishe</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===================== ANIMATED IG LOGO =====================
const AnimatedLogo = () => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.75, y: 30 }}
      animate={{ opacity: 1, scale: [1, 1.03, 1], y: [0, -6, 0] }}
      transition={{ duration: 5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
      style={{ margin: "0 auto 3rem auto", display: "flex", alignItems: "center", justifyContent: "center", background: "none" }}
    >
      {/* Glow halo removed */}
      {/* Spinning gradient ring — removed */}
      {/* White gap — removed so logo background shows through */}
      {/* Actual logo image */}
      <IgitaboLogo size={160} variant="icon" />
    </motion.div>
  );
};

// ===================== SPLASH SCREEN =====================
const SplashScreen = ({ onComplete }) => {
  useEffect(() => { const timer = setTimeout(onComplete, 2500); return () => clearTimeout(timer); }, [onComplete]);
  return (
    <motion.div className="splash-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}>
      <AnimatedLogo />
      <div className="splash-title">Murakaza neza kuri Inkingi...</div>
      <div className="splash-subtitle">kohereza no gukora raporo</div>
    </motion.div>
  );
};

const ResponsiveRuntimeEnhancements = () => {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const shouldLazyLoad = (img) => {
      if (!(img instanceof HTMLImageElement)) return false;
      if (img.hasAttribute("loading")) return false;
      if (img.closest("header, .navbar, .navbar-brand, .brand-logo, .brand-icon, .dx-logo, .splash-container, .home-page")) {
        return false;
      }

      const rect = img.getBoundingClientRect();
      return rect.top > window.innerHeight * 0.85 || rect.bottom < 0;
    };

    const enhanceImage = (img) => {
      if (!(img instanceof HTMLImageElement)) return;
      if (!img.getAttribute("decoding")) img.setAttribute("decoding", "async");
      if (shouldLazyLoad(img)) img.setAttribute("loading", "lazy");
    };

    const enhanceTree = (root = document) => {
      if (!root?.querySelectorAll) return;
      root.querySelectorAll("img").forEach(enhanceImage);
    };

    enhanceTree(document);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node instanceof HTMLImageElement) enhanceImage(node);
          enhanceTree(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const handleResize = () => enhanceTree(document);
    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return null;
};

const ResponsiveAppOverrides = () => (
  <style>{`
    :root {
      --app-safe-bottom: env(safe-area-inset-bottom, 0px);
      --app-safe-left: env(safe-area-inset-left, 0px);
      --app-safe-right: env(safe-area-inset-right, 0px);
    }

    :where(.dashboard-shell, .dashboard-container, .dx-shell, .broadcasts-root, .msg-container, .superadmin-layout, .responsive-page-shell) {
      width: 100%;
      max-width: 100%;
    }

    :where(.main-content, .dx-main, .dx-content, .msg-chat-area, .msg-sidebar, .superadmin-main, .responsive-data-region) {
      min-width: 0 !important;
    }

    :where(.table-responsive, .table-scroll-wrapper, .ab-table-scroll, .el-table-scroll, .ub-table-scroll, .responsive-data-region) {
      overflow-x: auto !important;
      overscroll-behavior-x: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-gutter: stable both-edges;
      width: 100%;
      max-width: 100%;
    }

    :where(.table-responsive table, .table-scroll-wrapper table, .ab-table-scroll table, .el-table-scroll table, .ub-table-scroll table, .responsive-data-region table) {
      width: max(100%, 640px) !important;
    }

    :where(.modal-dialog) {
      max-width: min(96vw, var(--bs-modal-width, 500px));
    }

    :where(.modal-content, .offcanvas, .dropdown-menu, .dx-modal) {
      max-width: 100%;
    }

    :where(.modal-body, .offcanvas-body, .card-body, .card-header, .card-footer) {
      min-width: 0;
    }

    :where(.modal-footer) {
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    :where(.modal-footer > *) {
      margin: 0 !important;
    }

    :where(.form-control, .form-select, .input-group, .btn, .btn-sm, .btn-lg, input[type="file"], input[type="date"]) {
      max-width: 100%;
    }

    :where(input[type="file"]) {
      overflow: hidden;
    }

    :where([class*="avatar"] img, [class*="photo"] img, [class*="profile"] img) {
      max-width: 100% !important;
      height: auto !important;
      object-fit: cover;
    }

    :where([class*="logo"] img) {
      max-width: 100% !important;
      object-fit: contain;
    }

    img:not([class*="logo"]):not(.app-logo-img) {
      max-width: 100%;
    }

    :where(.dx-bottom-nav, .bottom-nav, .mobile-bottom-nav) {
      padding-bottom: calc(8px + var(--app-safe-bottom));
    }

    @media (max-width: 1279px) {
      :where(.dx-summary-grid, [class*="summary-grid"]) {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
    }

    @media (max-width: 991.98px) {
      :where(.dashboard-container .main-content, .dashboard-shell .main-content, .dx-main, .superadmin-main) {
        margin-left: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
      }

      :where(.sidebar, .dx-sidebar, .superadmin-sidebar) {
        max-width: min(86vw, 320px) !important;
      }
    }

    @media (max-width: 767px) {
      :where(.container, .container-fluid, .main-content, .dx-content, .superadmin-main, .responsive-page-shell) {
        padding-left: max(0.75rem, var(--app-safe-left)) !important;
        padding-right: max(0.75rem, var(--app-safe-right)) !important;
      }

      :where(.row) {
        --bs-gutter-x: 0.875rem;
        --bs-gutter-y: 0.875rem;
      }

      :where(.col, [class*="col-"]) {
        min-width: 0;
      }

      :where(.modal-dialog) {
        margin: 0.5rem auto !important;
      }

      :where(.modal-footer) {
        flex-direction: column;
        align-items: stretch;
      }

      :where(.modal-footer > .btn, .modal-footer > button, .btn, button[type="submit"], button[type="button"]) {
        min-height: 44px;
      }

      :where(.dx-stats-grid, .dx-summary-grid, [class*="stats-grid"], [class*="summary-grid"]) {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }

      :where(.dx-quick-grid, [class*="quick-grid"]) {
        grid-template-columns: 1fr !important;
      }

      :where(.dx-topbar, .navbar, .topbar, .app-header) {
        flex-wrap: wrap;
      }

      :where(.dx-page-title, .page-title, .navbar-brand, h1, h2) {
        overflow-wrap: anywhere;
      }

      :where(.detail-layout, .details-layout, [class*="detail-layout"], [class*="detail-view"]) {
        display: grid !important;
        grid-template-columns: 1fr !important;
      }
    }

    @media (max-width: 479px) {
      :where(.dx-bottom-nav, .bottom-nav, .mobile-bottom-nav) {
        left: max(8px, var(--app-safe-left));
        right: max(8px, var(--app-safe-right));
        bottom: max(8px, var(--app-safe-bottom));
      }

      :where(.card, .glass-card, .dx-summary-card, .dx-stat-card, .modal-content, .offcanvas) {
        border-radius: 18px !important;
      }

      :where(.dx-topbar-actions, .topbar-actions, .header-actions, .pagination, .page-numbers, .pagination-controls) {
        flex-wrap: wrap;
        gap: 0.5rem;
      }
    }

    @media (max-width: 320px) {
      :where(.container, .container-fluid, .main-content, .dx-content, .superadmin-main, .responsive-page-shell) {
        padding-left: max(0.5rem, var(--app-safe-left)) !important;
        padding-right: max(0.5rem, var(--app-safe-right)) !important;
      }

      :where(.btn, button, .form-control, .form-select) {
        font-size: 0.95rem;
      }

      :where(.dx-bottom-item span, .nav-link, .sidebar .nav-link) {
        font-size: 0.62rem;
      }
    }

    @media (hover: none) and (pointer: coarse) {
      :where(.card:hover, .dx-stat-card:hover, .dx-quick-btn:hover, .nav-link:hover) {
        transform: none !important;
      }
    }
  `}</style>
);

// ===================== APP =====================
function App() {
  const [isSplashDone, setIsSplashDone] = useState(false);

  if (!isSplashDone) {
    return (
      <>
        <GlobalStyles />
        <SplashScreen onComplete={() => { setIsSplashDone(true); }} />
        <ResponsiveRuntimeEnhancements />
        <ResponsiveAppOverrides />
      </>
    );
  }

  return (
    <>
      <GlobalStyles />
      <NotificationProvider>
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/__debug_isuku" element={<IsukuIsukura />} />
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/select-role" element={<RoleSelectionPage mode="login" />} />
            <Route path="/select-role-register" element={<RoleSelectionPage mode="register" />} />
            <Route path="/akagari" element={<AkagariLoginPage />} />
            <Route path="/mudugudu" element={<InjiraBackground style={{minHeight:"100vh"}}><LoginForm title="Mudugudu" apiEndpoint="/login_mudugudu" redirectPath="/dashboard/Mudugudu" /></InjiraBackground>} />
            <Route path="/abandi" element={<InjiraBackground style={{minHeight:"100vh"}}><LoginForm title="Isibo / Miberehomyiza" apiEndpoint="/login_abandi" redirectPath="/dashboard/:role" /></InjiraBackground>} />
            <Route path="/isibo-login" element={<IsiboLoginPage />} />
            <Route path="/umutekano-login" element={<UmutekanoLogin />} />
            <Route path="/umutekano/dashboard" element={<UmutekanoDashboard />} />

            <Route path="/dashboard/:role" element={<RoleDashboard />} />
            <Route path="/sys-config-auth" element={<SuperAdminLogin />} />
            <Route path="/sys-root/dashboard" element={<SuperAdminDashboard />} />
            <Route path="/imibare-navigation" element={<ImibareNavigation />} />
            <Route path="/village-info" element={<VillageInfoSystem />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/view-report/:id" element={<ViewReport />} />
            <Route path="/sign-receiver/:id" element={<ReceiverSignaturePage />} />
            <Route path="/spreadsheet" element={<Spreadsheet />} />
            <Route path="/word-table" element={<WordTableEditor />} />
            <Route path="/debug-ejoheza" element={<UmusanzuEjoheza />} />
          </Routes>
        </AnimatePresence>
      </NotificationProvider>
      <ResponsiveRuntimeEnhancements />
      <ResponsiveAppOverrides />
      <PWAInstallBanner />
    </>
  );
}

export default App;

